import React, { useState, useEffect } from 'react';
import {
  Share2, Link2, Mail, Copy, Check, Lock, Unlock, Calendar, Eye,
  Users, Globe, X, Shield, ChevronDown, Send, Clock, AlertTriangle,
  ExternalLink, Trash2, RefreshCw
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareDashboardModal Component
 * Allows users to generate shareable links and send email invitations
 */
const ShareDashboardModal = ({ isOpen, onClose, dashboardType = 'disclosure_workbench', dashboardTitle = 'Dashboard' }) => {
  const [activeTab, setActiveTab] = useState('link'); // 'link' or 'invite'
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareId, setShareId] = useState('');
  const [existingShares, setExistingShares] = useState([]);
  
  // Link settings
  const [linkSettings, setLinkSettings] = useState({
    title: dashboardTitle,
    description: '',
    passwordProtected: false,
    password: '',
    expiresInDays: 30,
    includeData: true,
    allowedViews: ['overview', 'details']
  });
  
  // Invitation settings
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteAccessLevel, setInviteAccessLevel] = useState('viewer');
  const [inviteSent, setInviteSent] = useState(false);

  // Fetch existing shares
  useEffect(() => {
    if (isOpen) {
      fetchExistingShares();
    }
  }, [isOpen]);

  const fetchExistingShares = async () => {
    const data = await apiFetch(`${API}/api/dashboard/shares`);
    if (data?._error) {
      console.error('Error fetching shares:', data.message);
      return;
    }
    if (data.success) {
      setExistingShares(data.shares || []);
    }
  };

  const generateShareLink = async () => {
    setLoading(true);
    const data = await apiFetch(`${API}/api/dashboard/share`, {
      method: 'POST',
      body: {
        dashboard_type: dashboardType,
        title: linkSettings.title,
        description: linkSettings.description,
        password_protected: linkSettings.passwordProtected,
        password: linkSettings.password,
        expires_in_days: linkSettings.expiresInDays,
        include_data: linkSettings.includeData,
        allowed_views: linkSettings.allowedViews
      }
    });

    if (data?._error) {
      console.error('Error generating share link:', data.message);
      alert('Failed to generate share link');
    } else if (data.success) {
      setShareUrl(data.share_url);
      setShareId(data.share_id);
      fetchExistingShares();
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvitation = async () => {
    if (!inviteEmail || !shareId) {
      alert('Please generate a share link first and enter an email');
      return;
    }
    
    setLoading(true);
    const data = await apiFetch(`${API}/api/dashboard/share/invite`, {
      method: 'POST',
      body: {
        share_id: shareId,
        email: inviteEmail,
        message: inviteMessage,
        access_level: inviteAccessLevel
      }
    });

    if (data?._error) {
      console.error('Error sending invitation:', data.message);
      alert('Failed to send invitation');
    } else if (data.success) {
      setInviteSent(true);
      setInviteEmail('');
      setInviteMessage('');
      setTimeout(() => setInviteSent(false), 3000);
    }
    setLoading(false);
  };

  const revokeShare = async (shareIdToRevoke) => {
    if (!window.confirm('Are you sure you want to revoke this share link?')) return;
    
    const data = await apiFetch(`${API}/api/dashboard/share/${shareIdToRevoke}`, { method: 'DELETE' });
    if (data?._error) {
      console.error('Error revoking share:', data.message);
      return;
    }
    fetchExistingShares();
    if (shareIdToRevoke === shareId) {
      setShareUrl('');
      setShareId('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="share-dashboard-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Share2 size={24} />
            <div>
              <h2 className="text-xl font-bold">Share Dashboard</h2>
              <p className="text-emerald-100 text-sm">{linkSettings.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('link')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'link' 
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50' 
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            data-testid="share-tab-link"
          >
            <Link2 size={18} />
            Share Link
          </button>
          <button
            onClick={() => setActiveTab('invite')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'invite' 
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50' 
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            data-testid="share-tab-invite"
          >
            <Mail size={18} />
            Email Invite
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'link' && (
            <div className="space-y-6">
              {/* Link Settings */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Share Title</label>
                  <input
                    type="text"
                    value={linkSettings.title}
                    onChange={(e) => setLinkSettings({ ...linkSettings, title: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g., Q4 2024 ESG Dashboard"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description (Optional)</label>
                  <textarea
                    value={linkSettings.description}
                    onChange={(e) => setLinkSettings({ ...linkSettings, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    rows={2}
                    placeholder="Add a note for viewers..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Password Protection */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {linkSettings.passwordProtected ? (
                          <Lock size={18} className="text-emerald-600" />
                        ) : (
                          <Unlock size={18} className="text-slate-400" />
                        )}
                        <span className="text-sm font-medium text-slate-700">Password Protection</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={linkSettings.passwordProtected}
                          onChange={(e) => setLinkSettings({ ...linkSettings, passwordProtected: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                      </label>
                    </div>
                    {linkSettings.passwordProtected && (
                      <input
                        type="password"
                        value={linkSettings.password}
                        onChange={(e) => setLinkSettings({ ...linkSettings, password: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                        placeholder="Enter password"
                      />
                    )}
                  </div>

                  {/* Expiration */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={18} className="text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Link Expiration</span>
                    </div>
                    <select
                      value={linkSettings.expiresInDays}
                      onChange={(e) => setLinkSettings({ ...linkSettings, expiresInDays: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg"
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                      <option value={365}>1 year</option>
                    </select>
                  </div>
                </div>

                {/* Include Data Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Eye size={18} className="text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Include Live Data</p>
                      <p className="text-xs text-slate-500">Allow viewers to see actual dashboard data</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={linkSettings.includeData}
                      onChange={(e) => setLinkSettings({ ...linkSettings, includeData: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateShareLink}
                disabled={loading}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="generate-share-link-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Link2 size={18} />
                    Generate Share Link
                  </>
                )}
              </button>

              {/* Generated Link */}
              {shareUrl && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-emerald-700">Share Link Generated!</span>
                    {linkSettings.passwordProtected && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600">
                        <Lock size={12} /> Protected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded-lg text-sm font-mono text-slate-700"
                    />
                    <button
                      onClick={copyToClipboard}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                        copied 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                      }`}
                      data-testid="copy-share-link-btn"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-xs text-emerald-600 mt-2">
                    Expires in {linkSettings.expiresInDays} days
                  </p>
                </div>
              )}

              {/* Existing Shares */}
              {existingShares.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Active Share Links</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {existingShares.filter(s => s.is_active).map((share) => (
                      <div key={share.share_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Globe size={16} className="text-slate-400" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{share.title}</p>
                            <p className="text-xs text-slate-500">
                              {share.view_count} views • Expires {new Date(share.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => revokeShare(share.share_id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Revoke access"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invite' && (
            <div className="space-y-6">
              {!shareUrl && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Generate a share link first</p>
                    <p className="text-xs text-amber-600">Switch to the "Share Link" tab to create a shareable link before sending invitations.</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="colleague@company.com"
                    disabled={!shareUrl}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Access Level</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'viewer', label: 'Viewer', icon: Eye, desc: 'Can view only' },
                      { value: 'commenter', label: 'Commenter', icon: Users, desc: 'Can add comments' },
                      { value: 'editor', label: 'Editor', icon: Shield, desc: 'Can edit data' }
                    ].map((level) => {
                      const Icon = level.icon;
                      return (
                        <button
                          key={level.value}
                          onClick={() => setInviteAccessLevel(level.value)}
                          disabled={!shareUrl}
                          className={`p-3 rounded-lg border-2 text-left transition-colors ${
                            inviteAccessLevel === level.value
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-slate-200 hover:border-slate-300'
                          } ${!shareUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Icon size={18} className={inviteAccessLevel === level.value ? 'text-emerald-600' : 'text-slate-400'} />
                          <p className="text-sm font-medium text-slate-700 mt-1">{level.label}</p>
                          <p className="text-xs text-slate-500">{level.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Personal Message (Optional)</label>
                  <textarea
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                    placeholder="Add a personal note to include in the invitation email..."
                    disabled={!shareUrl}
                  />
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={sendInvitation}
                disabled={loading || !shareUrl || !inviteEmail}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-invite-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : inviteSent ? (
                  <>
                    <Check size={18} />
                    Invitation Sent!
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Invitation
                  </>
                )}
              </button>

              {inviteSent && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                  <Check size={24} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm font-medium text-emerald-700">Invitation sent successfully!</p>
                  <p className="text-xs text-emerald-600">The recipient will receive an email with access instructions.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Shared dashboards are read-only by default
          </p>
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDashboardModal;
